import { Icon } from '#/components/Icon/Icon';
import { BodyMicro } from '#/components/Text/BodyMicro';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ButtonGroup } from '#/modules/salaries/components/ButtonGroup/ButtonGroup';
import type { BarChart } from '#/modules/salaries/types/BarChart';
import type { LineChart } from '#/modules/salaries/types/LineChart';
import type { MainChartData } from '#/modules/salaries/types/MainChartData';
import { isDefined } from '#/utils/functional/type';

import { EstimatedRangeButton } from './EstimatedRangeButton';
import { MainChartLegendContainer } from './MainChartLegend.styled';
import { SubmissionsButton } from './SubmissionsButton';

type MainChartLegendProps = {
  data: MainChartData;
  setData: (arg0: MainChartData) => void;
};

export function MainChartLegend({ data, setData }: MainChartLegendProps) {
  const { i18n } = useI18n();

  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const activeChart = [data.lineChart, data.barChart].find(
    (c) => c?.active,
  ).name;

  return isDefined(data) ? (
    <MainChartLegendContainer>
      <ButtonGroup
        value={activeChart}
        onSelected={(e) =>
          setData({
            ...data,
            lineChart: {
              ...(data.lineChart as LineChart),
              active: e.currentTarget.id === 'estimatedRange',
            },
            barChart: {
              ...(data.barChart as BarChart),
              active: e.currentTarget.id === 'submissions',
            },
          })
        }
      >
        <EstimatedRangeButton
          variant="primary"
          type="button"
          id="estimatedRange"
          disabled={!data.lineChart}
          qaId="toggle-estimated-range-layer"
        >
          {isDefined(data.lineChart)
            ? 'Estimated Range'
            : 'Estimated Range Not Available'}
        </EstimatedRangeButton>
        <SubmissionsButton
          variant="primary"
          type="button"
          id="submissions"
          disabled={!data.barChart}
          qaId="toggle-submissions-layer"
        >
          <span>
            {isDefined(data.barChart)
              ? 'Submissions'
              : 'Submissions Not Available'}
          </span>
          {!isDefined(data.barChart) ? (
            <Tooltip
              size="large"
              placement="top-left"
              content={
                <BodyMicro
                  letterSpacing={0}
                  fontWeight="normal"
                  lineHeight="normal"
                >
                  {i18n(
                    "We've collected submissions related to this title and similar titles, but there are fewer than 20 submissions matching your search criteria. Our machine learning system can estimate an expected salary range for this title, but there are not enough submissions to show meaningful results.",
                  )}
                </BodyMicro>
              }
            >
              <Icon
                name="question-circle"
                colorName="mediumContentGrey"
                size={19}
              />
            </Tooltip>
          ) : null}
        </SubmissionsButton>
      </ButtonGroup>
    </MainChartLegendContainer>
  ) : null;
}
